<template>
    <div :class="classes" @click="toggleEditMode">
        <template v-if="!editMode">
            <CompassSkeleton
                v-if="loading"
                size="x-large"
                :style="{ width: `${componentWidth}px` }"
            />
            <div v-else ref="editSelectValue" :class="valueClasses">
                <CompassTooltip
                    :content="tooltipContent"
                    :disabled="!tooltipContent"
                    placement="bottom-start"
                    :arrow="false"
                >
                    <CompassTag
                        v-if="showTag"
                        :color="selectedItem.color"
                        :icon="selectedItem.icon"
                    >
                        {{ selectedItem.label }}
                    </CompassTag>
                    <span v-else class="typographies-paragraph-medium-regular">
                        {{ selectedItem.label }}
                    </span>
                </CompassTooltip>
            </div>
        </template>
        <CompassSelect
            v-else
            ref="editSelect"
            :size="size"
            :value="selectedValue"
            :filterable="filterable"
            :actions="actions"
            forceSelectOpen
            :type="showTag ? 'tag' : 'text'"
            @close="editMode = false"
            @update:value="handleValueUpdate"
        >
            <CompassDropdownSelectOption
                v-for="item in items"
                :key="item.id"
                :label="item.label"
                :value="item.id"
                :color="item.color"
                :tagIcon="item.icon"
            >
                <CompassTag v-if="showTag" :color="item.color" :icon="item.icon" size="small">
                    {{ item.label }}
                </CompassTag>
            </CompassDropdownSelectOption>
        </CompassSelect>
    </div>
</template>
<script>
import { arrow } from '@popperjs/core';

export default {
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        items: {
            type: Array,
            default: null,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        size: {
            type: String,
            default: 'medium',
        },
        showTag: {
            type: Boolean,
            default: false,
        },
        filterable: {
            type: Boolean,
            default: false,
        },
        forceClose: {
            type: Boolean,
            default: false,
        },
        actions: {
            type: Array,
            default: () => [],
        },
        tooltip: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            editMode: false,
            componentWidth: 0,
        };
    },
    computed: {
        selectedItem() {
            return this.items.find((item) => item.selected);
        },
        selectedValue() {
            return this.selectedItem.id;
        },
        classes() {
            return [
                'edit-select',
                `edit-select--${this.size}`,
                this.canEdit && this.items.length > 1 ? 'edit-select--editable' : '',
            ];
        },
        valueClasses() {
            return ['edit-select__value', `edit-select__value--${this.size}`];
        },
        tooltipContent() {
            if (this.items.length === 1) {
                return this.$t.__('editInPlace.select.noItemsTooltip');
            }

            return this.tooltip;
        },
    },
    watch: {
        editMode(newValue) {
            if (this.$refs.editSelectValue) {
                this.setComponentWidth();
            }
            this.$emit('edit', newValue);
            this.$nextTick(() => {
                if (this.$refs.editSelect) {
                    this.$refs.editSelect.$el.querySelector(
                        '.compass-input__inner',
                    ).style.width = `${this.componentWidth}px`;
                }
            });
        },
        forceClose(newValue) {
            if (newValue && this.editMode) {
                this.editMode = false;
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.setComponentWidth();
        });
    },
    methods: {
        setComponentWidth() {
            let width = 0;
            const gapSize = 8;

            if (!this.$refs.editSelectValue) {
                return;
            }

            for (const child of this.$refs.editSelectValue.children) {
                width += child.offsetWidth;
            }

            this.componentWidth = width + gapSize;
        },
        toggleEditMode() {
            if (!this.editMode && this.canEdit) {
                this.editMode = true;
            }
        },
        handleValueUpdate(value) {
            this.$emit('update', value);
            this.editMode = false;
        },
    },
};
</script>
<style scoped lang="scss">
.edit-select {
    border-radius: 2px;
    position: relative;

    &--editable {
        .edit-select__value {
            cursor: pointer;

            &:before {
                content: '';
                background: var(--colors-common-background-primary-hover-alt);
                border-radius: 2px;
                bottom: 0;
                display: none;
                left: -4px;
                position: absolute;
                right: -4px;
                top: 0;
            }

            &:hover {
                &:before {
                    display: block;
                }
            }

            &::v-deep {
                > * {
                    position: relative;
                }
            }
            & > * {
                position: relative;
            }
        }
    }

    &--small {
        &::v-deep {
            .compass-skeleton__line {
                height: var(--spacing-4);
            }
        }
    }

    &__value {
        align-items: center;
        display: flex;
        gap: var(--spacing-1);
        min-height: 32px;

        &--small {
            min-height: 24px;
        }
    }
}
</style>
